import { ButtonTypes, ButtonSizes, ButtonColors, ButtonVariants } from '@/constants/buttonTypes';
import { CircularProgressSize } from '@/constants/circularProgressTypes';
import { SteppedLoaderImageKeys } from '@/constants/loaderTypes';
import { CardBorderRadius, CardBorderColor, CardShadows } from '@/constants/cardTypes';
import { CardContentPadding } from '@/constants/cardContentTypes';
import { Colors } from '@/constants/colorTypes';
import { ComponentsTypes } from '@/constants/componentsTypes';
import { DividerColors, DividerTypes } from '@/constants/dividerTypes';
import { IconSizes, IconColors } from '@/constants/iconTypes';
import { VinImageIds } from '@/constants/imageTypes';
import {
  LayoutAlignItems,
  LayoutJustifyContent,
  LayoutDirection,
  LayoutMediaQuery,
  LayoutPadding,
} from '@/constants/layoutTypes';
import { LinkTypes } from '@/constants/linkTypes';
import { ListItemTypes } from '@/constants/listItemTypes';
import { ListColors } from '@/constants/listTypes';
import {
  TypographyColors,
  TypographyAlign,
  TypographyVariants,
  TypographyFontWeight,
} from '@/constants/typographyTypes';
import { DataType, ValidationType } from '@/constants/validationTypes';
import { z } from 'zod/v4';
import { AlertAnchorOrigin } from '@/constants/alertAnchorOrigin';
import { AlertSeverity } from '@/constants/alertTypes.tsx';
import { coverage, priceCardDetails, quoteState } from './quoteResponseSchema';
import { ChipColors, ChipSizes, ChipVariant } from '@/constants/chipTypes';
import { QuoteKind, QuoteStatus } from '@/constants/quoteTypes';
import { PricingTermArray } from '@/constants/pricingTypes';

const dataTypeRule = z.object({
  type: z.literal(ValidationType.DataType),
  value: z.enum(DataType),
});

const requiredTypeRule = z.object({
  type: z.literal(ValidationType.Required),
});

const mustBeTrueTypeRule = z.object({
  type: z.literal(ValidationType.MustBeTrue),
});

const invalidTypeRule = z.object({
  type: z.literal(ValidationType.Invalid),
  message: z.string(),
});

const minTypeRule = z.object({
  type: z.literal(ValidationType.Min),
  value: z.union([z.string(), z.number()]),
});

const maxTypeRule = z.object({
  type: z.literal(ValidationType.Max),
  value: z.union([z.string(), z.number()]),
});

const allowedTypeRule = z.object({
  type: z.literal(ValidationType.AllowedValues),
  value: z.array(z.union([z.string(), z.boolean(), z.number()])),
});

const regexTypeRule = z.object({
  type: z.literal(ValidationType.Regex),
  value: z.string(),
  message: z.string().nullable().default(null),
});

const notAllowedTypeRule = z.object({
  type: z.literal(ValidationType.NotAllowedValues),
  value: z.array(z.union([z.string(), z.boolean(), z.number()])),
});

const validationRule = z.discriminatedUnion('type', [
  dataTypeRule,
  requiredTypeRule,
  mustBeTrueTypeRule,
  invalidTypeRule,
  minTypeRule,
  maxTypeRule,
  allowedTypeRule,
  regexTypeRule,
  notAllowedTypeRule,
]);

const fieldCore = z.object({
  attribute: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  entity_index: z.number(),
  scope: z.string(),
  message: z.string().nullable(),
  valid: z.boolean().nullable(),
  masked: z.boolean(),
  ignored_data_validations: z.array(z.string()).optional(),
  validation_rules: z.array(validationRule),
  persist_on_blur: z.boolean().optional(),
});

const optionValue = z.union([z.string(), z.number(), z.boolean()]);

const option = z.object({
  label: z.string(),
  sublabel: z.string().nullable(),
  value: optionValue,
});

const braintreeInput = fieldCore.extend({
  type: z.literal(ComponentsTypes.BRAINTREE_INPUT),
  client_token: z.string(),
  exclude_applepay: z.boolean(),
  legal: z.string(),
  amount: z.string(),
  value: z.string().nullable(),
});

const dateInputSegmented = fieldCore.extend({
  type: z.literal(ComponentsTypes.DATE_INPUT_SEGMENTED),
  value: z.string().nullable(),
});

const datePicker = fieldCore.extend({
  type: z.literal(ComponentsTypes.DATE_PICKER),
  value: z.string().nullable(),
});

const numberInput = fieldCore.extend({
  type: z.literal(ComponentsTypes.NUMBER_INPUT),
  value: z.number().nullable(),
});

const selectValue = z.union([z.string(), z.number(), z.null(), z.boolean()]);

const radio = fieldCore.extend({
  type: z.literal(ComponentsTypes.RADIO),
  value: selectValue,
  options: z.array(option),
});

const chipGroup = fieldCore.extend({
  type: z.literal(ComponentsTypes.CHIP_GROUP),
  value: selectValue,
  options: z.array(option),
});

const selectDropdown = fieldCore.extend({
  type: z.literal(ComponentsTypes.SELECT_DROPDOWN),
  value: selectValue,
  options: z.array(option),
});

const autocompleteDropdown = fieldCore.extend({
  type: z.literal(ComponentsTypes.AUTOCOMPLETE_DROPDOWN),
  value: selectValue,
  options: z.array(option),
});

const switchSelect = fieldCore.extend({
  type: z.literal(ComponentsTypes.SWITCH),
  value: selectValue,
  options: z.array(option),
});

const checkbox = fieldCore.extend({
  type: z.literal(ComponentsTypes.CHECKBOX),
  value: z.boolean().nullable(),
});

const textInput = fieldCore.extend({
  type: z.literal(ComponentsTypes.TEXT_INPUT),
  value: z.string().nullable(),
});

const emailInput = fieldCore.extend({
  type: z.literal(ComponentsTypes.EMAIL_INPUT),
  value: z.string().nullable(),
});

const phoneInput = fieldCore.extend({
  type: z.literal(ComponentsTypes.PHONE_INPUT),
  value: z.string().nullable(),
  auto_format: z.boolean().optional(),
});

const navigationListItem = z.object({
  type: z.literal(ListItemTypes.NAVIGATION),
  icon: z.string().nullable(),
  overline: z.string().nullable(),
  label1: z.string(),
  label2: z.string().nullable(),
  supporting_text: z.string().nullable(),
  with_dividers: z.boolean().nullable(),
  icon_right: z.string().nullable(),
  step_id: z.string(),
  entity_index: z.number(),
  trailing_pill: z.string().nullable().optional(),
});

const hrefListItem = z.object({
  type: z.literal(ListItemTypes.HREF),
  icon: z.string().nullable(),
  overline: z.string().nullable(),
  label1: z.string(),
  label2: z.string().nullable(),
  supporting_text: z.string().nullable(),
  with_dividers: z.boolean().nullable(),
  icon_right: z.string().nullable(),
  href: z.string().nullable(),
  is_loading: z.boolean().nullable(),
  trailing_pill: z.string().nullable().optional(),
});

const inertListItem = z.object({
  type: z.literal(ListItemTypes.INERT),
  icon: z.string().nullable(),
  overline: z.string().nullable(),
  label1: z.string(),
  label2: z.string().nullable(),
  supporting_text: z.string().nullable(),
  with_dividers: z.boolean().nullable(),
  trailing_text: z.string().nullable(),
  trailing_pill: z.string().nullable().optional(),
});

const buttonCore = z.object({
  id: z.string(),
  label: z.string(),
  size: z.enum(ButtonSizes),
  color: z.enum(ButtonColors),
  start_icon: z.string().nullable(),
  end_icon: z.string().nullable(),
  variant: z.enum(ButtonVariants),
  full_width: z.boolean().nullable().optional(),
});

const navigationButton = buttonCore.extend({
  type: z.literal(ButtonTypes.NAVIGATION),
  step_id: z.string(),
  entity_index: z.number(),
});

const backButton = buttonCore.extend({
  type: z.literal(ButtonTypes.BACK),
});

const actionButton = buttonCore.extend({
  type: z.literal(ButtonTypes.ACTION),
  action: z.string(),
  success_toast_id: z.string().nullable(),
  error_toast_id: z.string().nullable(),
  arguments: z.record(z.string(), z.unknown()).nullable(),
});

const submitButton = buttonCore.extend({
  type: z.literal(ButtonTypes.SUBMIT),
  form_id: z.string(),
});

const validationModalSubmitButton = buttonCore.extend({
  type: z.literal(ButtonTypes.VALIDATION_MODAL_SUBMIT),
  form_id: z.string(),
  modal_id: z.string(),
  validation_scopes: z.array(z.string()),
});

export const coverageSubmitButton = buttonCore.extend({
  type: z.literal(ButtonTypes.COVERAGE_SUBMIT),
  form_id: z.string(),
});

const modalButton = buttonCore.extend({
  type: z.literal(ButtonTypes.MODAL),
  modal_id: z.string(),
  method_type: z.enum(['open', 'close', 'toggle']),
});

const hrefButton = buttonCore.extend({
  type: z.literal(ButtonTypes.HREF),
  href: z.string(),
});

const reloadButton = buttonCore.extend({
  type: z.literal(ButtonTypes.RELOAD),
});

const button = z.discriminatedUnion('type', [
  navigationButton,
  submitButton,
  validationModalSubmitButton,
  coverageSubmitButton,
  modalButton,
  reloadButton,
  hrefButton,
  backButton,
  actionButton,
]);

const skeleton = z.object({
  type: z.literal(ComponentsTypes.SKELETON),
});

const standardDivider = z.object({
  type: z.literal(DividerTypes.STANDARD_DIVIDER),
});

const accentDivider = z.object({
  type: z.literal(DividerTypes.ACCENT_DIVIDER),
  color: z.enum(DividerColors),
});

const divider = z.discriminatedUnion('type', [standardDivider, accentDivider]);

const image = z.object({
  type: z.literal(ComponentsTypes.IMAGE),
  id: z.string(),
  alt: z.string().nullable(),
  width: z.string().nullable(),
  margin: z.string().nullable(),
  background_color: z.enum(Colors).nullable(),
});

const vinImage = z.object({
  type: z.literal(ComponentsTypes.VIN_IMAGE),
  id: z.enum(VinImageIds),
  alt: z.string().nullable(),
  width: z.string().nullable(),
});

const externalImage = z.object({
  type: z.literal(ComponentsTypes.EXTERNAL_IMAGE),
  src: z.string().nullable(),
  alt: z.string().nullable(),
  width: z.string().nullable(),
  margin: z.string().nullable(),
});

const loader = z.object({
  type: z.literal(ComponentsTypes.LOADER),
});

const step = z.object({
  type: z.literal(ComponentsTypes.STEP),
  text: z.string(),
  image: z.enum(SteppedLoaderImageKeys),
  duration: z.number(),
});

const steppedLoader = z.object({
  type: z.literal(ComponentsTypes.STEPPED_LOADER),
  steps: z.array(step),
});

const successAnimation = z.object({
  type: z.literal(ComponentsTypes.SUCCESS_ANIMATION),
});

const circularProgress = z.object({
  type: z.literal(ComponentsTypes.CIRCULAR_PROGRESS),
  size: z.enum(CircularProgressSize),
});

const toast = z.object({
  type: z.literal(ComponentsTypes.TOAST),
  severity: z.enum(AlertSeverity),
  id: z.string(),
  status: z.enum(['open', 'closed']),
  text: z.string().nullable(),
  duration: z.number(),
  anchor_origin: z.enum(AlertAnchorOrigin),
});

const typographyCore = z.object({
  variant: z.enum(TypographyVariants),
  color: z.enum(TypographyColors),
  font_weight: z.enum(TypographyFontWeight).nullable(),
  align: z.enum(TypographyAlign),
  text: z.string(),
  store_fields: z.array(z.object({ scope: z.string(), entity_index: z.number(), attribute: z.string() })).optional(),
});

const typography = typographyCore.extend({
  type: z.literal(ComponentsTypes.TYPOGRAPHY),
});

const cookieManagementLink = typographyCore.extend({
  type: z.literal(LinkTypes.COOKIE_MANAGEMENT_LINK),
});

const hrefLink = typographyCore.extend({
  type: z.literal(LinkTypes.HREF),
  href: z.string(),
});

const scrollLink = typographyCore.extend({
  type: z.literal(LinkTypes.SCROLL),
  scroll_id: z.string(),
});

const navigationLink = typographyCore.extend({
  type: z.literal(LinkTypes.NAVIGATION),
  step_id: z.string(),
  entity_index: z.number(),
});

const modalLink = typographyCore.extend({
  type: z.literal(LinkTypes.MODAL),
  modal_id: z.string(),
  method_type: z.enum(['open', 'close', 'toggle']),
});

const lineBreak = z.object({
  type: z.literal(ComponentsTypes.LINE_BREAK),
});

const typographies = z.discriminatedUnion('type', [
  typography,
  cookieManagementLink,
  hrefLink,
  scrollLink,
  navigationLink,
  modalLink,
  lineBreak,
]);

const richTextCheckbox = fieldCore.extend({
  type: z.literal(ComponentsTypes.RICH_TEXT_CHECKBOX),
  label: z.string().default(''), // TODO: label is required by the base field component but unused here — remove once label is optional
  value: z.boolean().nullable(),
  spans: z.array(typographies),
  variant: z.enum(TypographyVariants),
  align_items: z.enum(['start', 'center']),
});

const field = z.discriminatedUnion('type', [
  dateInputSegmented,
  datePicker,
  braintreeInput,
  numberInput,
  radio,
  chipGroup,
  selectDropdown,
  autocompleteDropdown,
  switchSelect,
  textInput,
  emailInput,
  phoneInput,
  checkbox,
  richTextCheckbox,
]);

const coverageChipGroup = z.object({
  type: z.literal(ComponentsTypes.COVERAGE_CHIP_GROUP),
  symbol: z.string(),
  scope: z.string(),
  kind: z.string(),
});
const coverageSelectDropdown = z.object({
  type: z.literal(ComponentsTypes.COVERAGE_SELECT_DROPDOWN),
  symbol: z.string(),
  scope: z.string(),
  kind: z.string(),
  label: z.string(),
});

const coverageCheckbox = z.object({
  type: z.literal(ComponentsTypes.COVERAGE_CHECKBOX),
  symbol: z.string(),
  scope: z.string(),
  kind: z.string(),
});

const readonlyCoverageValue = z.object({
  type: z.literal(ComponentsTypes.READONLY_COVERAGE_VALUE),
  symbol: z.string(),
  scope: z.string(),
  kind: z.string(),
  label: z.string(),
});

const readonlyCoverageCheckbox = z.object({
  type: z.literal(ComponentsTypes.READONLY_COVERAGE_CHECKBOX),
  symbol: z.string(),
  scope: z.string(),
  kind: z.string(),
});

const readonlyCoverageChipGroup = z.object({
  type: z.literal(ComponentsTypes.READONLY_COVERAGE_CHIP_GROUP),
  symbol: z.string(),
  scope: z.string(),
  kind: z.string(),
});

const readonlyTextInput = z.object({
  type: z.literal(ComponentsTypes.READONLY_TEXT_INPUT),
  label: z.string(),
  value: z.string(),
});

const readonlyDateInputSegmented = z.object({
  type: z.literal(ComponentsTypes.READONLY_DATE_INPUT_SEGMENTED),
  label: z.string(),
  value: z.string().nullable(),
});

const readonlyDatePicker = z.object({
  type: z.literal(ComponentsTypes.READONLY_DATE_PICKER),
  label: z.string(),
  value: z.string(),
});

const readonlySelectDropdown = z.object({
  type: z.literal(ComponentsTypes.READONLY_SELECT_DROPDOWN),
  label: z.string(),
  value: z.string(),
});

const coverageField = z.discriminatedUnion('type', [
  coverageChipGroup,
  coverageSelectDropdown,
  coverageCheckbox,
  readonlyCoverageValue,
  readonlyCoverageCheckbox,
  readonlyCoverageChipGroup,
]);

const infoCard = z.object({
  type: z.literal(ComponentsTypes.INFO_CARD),
  headline: z.string(),
  body: z.string(),
});

const priceCardFields = {
  price_estimate_modal_id: z.string(),
  profile_modal_id: z.string(),
  payment_details_modal_id: z.string(),
  form_id: z.string(),
};

const priceCard = z.object({
  type: z.literal(ComponentsTypes.PRICE_CARD),
  ...priceCardFields,
  nudge_text: z.string().nullable().optional(),
  nudge_scroll_target_id: z.string().nullable().optional(),
});

const partnerCommitmentPriceCard = z.object({
  type: z.literal(ComponentsTypes.PARTNER_COMMITMENT_PRICE_CARD),
  ...priceCardFields,
});

const address = z.object({
  type: z.literal(ComponentsTypes.ADDRESS),
  address1: textInput,
  address2: textInput,
  city: textInput,
  state: autocompleteDropdown,
  zip: textInput,
  allow_state_changes: z.boolean().optional(),
  cross_state_modal_id: z.string().nullable().optional(),
  cross_state_helper_text: z.array(typographies).nullable().optional(),
});

const altAddress = z.object({
  type: z.literal(ComponentsTypes.ALT_ADDRESS),
  address1: textInput,
  address2: textInput,
  city: textInput,
  state: autocompleteDropdown,
  zip: textInput,
  validation_modal_id: z.string(),
});

const annualizedMileage = z.object({
  type: z.literal(ComponentsTypes.ANNUALIZED_MILEAGE),
  annualized_mileage: numberInput,
  commuting_weeks: z.number().optional(),
  primary_usage: textInput,
  commute_distance: numberInput,
  business_mileage: z.number(),
  occasional_mileage: z.number(),
  rideshare_mileage_full_time: z.number(),
  rideshare_mileage_part_time: z.number(),
});

const toggleListItem = z.object({
  type: z.literal(ListItemTypes.TOGGLE),
  icon: z.string().nullable(),
  overline: z.string().nullable(),
  label1: z.string(),
  label2: z.string().nullable(),
  with_dividers: z.boolean().nullable(),
  supporting_text: z.object({
    static: z.string().nullable(),
    on: z.string().nullable(),
    off: z.string().nullable(),
  }),
  toggle_component: z.discriminatedUnion('type', [checkbox, switchSelect]).nullable(),
  entity_index: z.number(),
  trailing_pill: z.string().nullable().optional(),
});

const creditCardBrandLogo = z.object({
  type: z.literal(ComponentsTypes.CREDIT_CARD_BRAND_LOGO),
  card_type: z.string().nullable(),
});

const inertIcon = z.object({
  type: z.literal(ComponentsTypes.INERT_ICON),
  icon_name: z.string(),
  size: z.enum(IconSizes),
  color: z.enum(IconColors),
  filled: z.boolean().optional(),
});

const inertChip = z.object({
  type: z.literal(ComponentsTypes.INERT_CHIP),
  label: z.string(),
  size: z.enum(ChipSizes),
  color: z.enum(ChipColors),
  variant: z.enum(ChipVariant),
});

const entityTarget = z.object({
  scope: z.string(),
  entity_index: z.number(),
});

const entityInProgressChip = z.object({
  type: z.literal(ComponentsTypes.ENTITY_IN_PROGRESS_CHIP),
  scope: z.string(),
  entity_index: z.number(),
});

const price = z.object({
  type: z.literal(ComponentsTypes.PRICE),
  variant: z.enum(TypographyVariants),
});

const partnerCommitmentPrice = z.object({
  type: z.literal(ComponentsTypes.PARTNER_COMMITMENT_PRICE),
  variant: z.enum(TypographyVariants),
});

const richText = z.object({
  align: z.enum(TypographyAlign),
  type: z.literal(ComponentsTypes.RICH_TEXT),
  spans: z.array(typographies),
  variant: z.enum(TypographyVariants),
});

const alert = z.object({
  type: z.literal(ComponentsTypes.ALERT),
  severity: z.enum(AlertSeverity),
  title: z.string().nullable(),
  title_gutter_bottom: z.boolean().nullable(),
  text: z.string().nullable(),
  spans: z.array(typographies).nullable(),
});

const tab = z.object({
  type: z.literal(ComponentsTypes.TAB),
  label: z.string(),
  tab_id: z.number(),
});

const navigationStep = z.object({
  label: z.string(),
  step_id: z.string(),
  entity_index: z.number(),
  errored: z.boolean(),
  navigable: z.boolean(),
  completed: z.boolean(),
  validation_scopes: z.array(z.string()).optional(),
});

const navigationStepper = z.object({
  type: z.literal(ComponentsTypes.NAVIGATION_STEPPER),
  steps: z.array(navigationStep),
});

const driverSchema = z.object({
  driver: z.string().nullable(),
  first_licensed: z.number().int().nullable(),
  license_state: z.string().nullable(),
  birth_date: z.string().nullable(),
});

const vehicleSchema = z.object({
  year: z.number().nullable(),
  make: z.string().nullable(),
  model: z.string().nullable(),
  primary_use: z.string().nullable(),
  annual_mileage: z.number().nullable(),
});

const prefillSchema = z.object({
  contact: z.string().nullable(),
  zip: z.string().nullable(),
  home_ownership: z.union([z.literal(0), z.literal(1)]).nullable(),
  drivers: z.array(driverSchema).nullable(),
  vehicles: z.array(vehicleSchema).nullable(),
});

const mediaAlphaResell = z.object({
  type: z.enum([ComponentsTypes.MEDIA_ALPHA_RESELL]),
  placement_id: z.string().nullable(),
  policyholder_driver_id: z.string().nullable(),
  prefill_data: prefillSchema.nullable(),
});

const searchBar = z.object({
  type: z.literal(ComponentsTypes.SEARCH_BAR),
  label: z.string(),
  options: z.array(option),
});

export type Tab = z.infer<typeof tab>;

export const simpleComponent = z.discriminatedUnion('type', [
  field,
  coverageField,
  button,
  skeleton,
  divider,
  address,
  altAddress,
  annualizedMileage,
  image,
  externalImage,
  typographies,
  loader,
  steppedLoader,
  successAnimation,
  circularProgress,
  alert,
  toast,
  tab,
  navigationListItem,
  hrefListItem,
  inertListItem,
  toggleListItem,
  infoCard,
  price,
  partnerCommitmentPrice,
  priceCard,
  partnerCommitmentPriceCard,
  richText,
  vinImage,
  creditCardBrandLogo,
  inertIcon,
  inertChip,
  entityInProgressChip,
  readonlyTextInput,
  readonlyDateInputSegmented,
  readonlyDatePicker,
  readonlySelectDropdown,
  mediaAlphaResell,
  navigationStepper,
  searchBar,
]);

// Note: Coverage Scene Wrapper and Mock Data Wrapper need to match
const coverageSceneWrapper = z.object({
  type: z.literal(ComponentsTypes.COVERAGE_SCENE_WRAPPER),
  initial_state: quoteState,
  get components() {
    return components;
  },
});

const mockDataWrapper = z.object({
  type: z.literal(ComponentsTypes.MOCK_DATA_WRAPPER),
  initial_state: quoteState,
  get components() {
    return components;
  },
});

export const modalSchema = z.object({
  id: z.string(),
  type: z.literal(ComponentsTypes.MODAL),
  status: z.enum(['open', 'closed']),
  sticky_footer: z.boolean(),
  get modal_body() {
    return components;
  },
  get modal_footer() {
    return components.nullable();
  },
});

export const lienholderConfirmationModal = z.object({
  id: z.string(),
  type: z.literal(ComponentsTypes.LIENHOLDER_CONFIRMATION_MODAL),
  coverage_symbols: z.array(z.string()),
});

const validationErrorModal = z.object({
  type: z.literal(ComponentsTypes.VALIDATION_ERROR_MODAL),
  id: z.string(),
  scope: z.string(),
  attribute: z.string(),
  entity_index: z.number().nullable(),
  error_pattern: z.string(),
  modal_title: z.string(),
  get modal_body() {
    return components;
  },
  get modal_footer() {
    return components.nullable();
  },
  entity_display: z.string().nullable(),
  dismiss_label: z.string().nullable(),
});

const coverageSection = z.object({
  type: z.literal(ComponentsTypes.COVERAGE_SECTION),
  symbol: z.string(),
  apply_to: z.string(),
  title: z.string(),
  description: z.string(),
  icon: z.string(),
  modal_link: modalLink.nullable(),
  modal: modalSchema.nullable(),
  get attribute_components() {
    return components;
  },
  confirmation_modal_id: z.string().nullable().optional(),
});

const baseInputFormSchema = z.object({
  id: z.string().nullable(),
  get components() {
    return components;
  },
});

export const inputForm = baseInputFormSchema.extend({
  type: z.literal(ComponentsTypes.FORM),
  validate_on_mount: z.boolean().optional(),
  validation_mode: z.enum(['onSubmit', 'onChange']).optional(),
});

export const actionForm = baseInputFormSchema.extend({
  action_name: z.string(),
  type: z.literal(ComponentsTypes.ACTION_FORM),
});

export const bindForm = baseInputFormSchema.extend({
  action_name: z.string(),
  type: z.literal(ComponentsTypes.BIND_FORM),
});

export const policyEffectiveDatePicker = z.object({
  type: z.literal(ComponentsTypes.POLICY_EFFECTIVE_DATE_PICKER),
});

export const form = z.discriminatedUnion('type', [inputForm, actionForm, bindForm]);

const containerSchema = z.object({
  id: z.string().nullable(),
  get components() {
    return components;
  },
});

const entityValidityRenderer = z.object({
  type: z.literal(ComponentsTypes.ENTITY_VALIDITY_RENDERER),
  entity_targets: z.array(entityTarget),
  get valid() {
    return component;
  },
  get invalid() {
    return component;
  },
});

const layoutSchema = containerSchema.extend({
  direction: z.enum([LayoutDirection.ROW, LayoutDirection.COLUMN]),
  spacing: z.number(),
  align_items: z.enum([
    LayoutAlignItems.START,
    LayoutAlignItems.CENTER,
    LayoutAlignItems.END,
    LayoutAlignItems.STRETCH,
    LayoutAlignItems.BASELINE,
  ]),
  justify_content: z.enum([
    LayoutJustifyContent.START,
    LayoutJustifyContent.CENTER,
    LayoutJustifyContent.END,
    LayoutJustifyContent.SPACE_BETWEEN,
  ]),
  media_query: z.enum([LayoutMediaQuery.HIDE_ON_SMALL_SCREENS, LayoutMediaQuery.HIDE_ON_LARGE_SCREENS]).nullable(),
  padding: z.enum([LayoutPadding.SMALL, LayoutPadding.LARGE]).nullable(),
  type: z.literal(ComponentsTypes.LAYOUT),
});

const card = containerSchema.extend({
  type: z.literal(ComponentsTypes.CARD),
  border: z.boolean(),
  border_radius: z.enum(CardBorderRadius),
  border_color: z.enum(CardBorderColor).nullable(),
  background_color: z.enum(Colors).nullable(),
  shadow: z.enum(CardShadows),
});

const cardContent = containerSchema.extend({
  type: z.literal(ComponentsTypes.CARD_CONTENT),
  padding: z.enum(CardContentPadding),
});

const readMore = containerSchema.extend({
  type: z.literal(ComponentsTypes.READ_MORE),
});

const sectionContainer = containerSchema.extend({
  type: z.literal(ComponentsTypes.SECTION_CONTAINER),
});

const tabList = z.object({
  type: z.literal(ComponentsTypes.TAB_LIST),
  tab_list_id: z.string(),
  get tabs() {
    return z.array(tab);
  },
});

const tabPanel = containerSchema.extend({
  type: z.literal(ComponentsTypes.TAB_PANEL),
  tab_id: z.number(),
  tab_list_id: z.string(),
});

const accordion = containerSchema.extend({
  type: z.literal(ComponentsTypes.ACCORDION),
  summary: z.string(),
});

const accordionList = z.object({
  type: z.literal(ComponentsTypes.ACCORDION_LIST),
  get accordions() {
    return z.array(accordion);
  },
});

const fixedContainer = containerSchema.extend({
  type: z.literal(ComponentsTypes.FIXED_CONTAINER),
});

const carouselTile = containerSchema.extend({
  type: z.literal(ComponentsTypes.CAROUSEL_TILE),
  padding: z.number(),
});

const carousel = z.object({
  type: z.literal(ComponentsTypes.CAROUSEL),
  padding: z.number(),
  get tiles() {
    return z.array(carouselTile);
  },
});

const expandableContainer = z.object({
  type: z.literal(ComponentsTypes.EXPANDABLE_CONTAINER),
  name: z.string(),
  get summary() {
    return components;
  },
  get details() {
    return components;
  },
});

const expandableList = z.object({
  type: z.literal(ComponentsTypes.EXPANDABLE_LIST),
  name: z.string().nullable(),
  get components() {
    return z.array(expandableContainer);
  },
});

const dynamicLayout = z.object({
  type: z.literal(ComponentsTypes.DYNAMIC_LAYOUT),
  get header() {
    return components;
  },
  get main() {
    return components;
  },
  get aside() {
    return components;
  },
});

const slot = z.object({
  type: z.literal(ComponentsTypes.SLOT),
  id: z.string(),
  get components() {
    return components;
  },
});

const eventCommunicator = z.object({
  event: z.string(),
  payload: z.record(z.string(), z.string()),
});

const list = containerSchema.extend({
  type: z.literal(ComponentsTypes.LIST),
  list_type: z.enum(['unordered', 'ordered']),
  color: z.enum(ListColors),
  columns: z.number().int().positive().nullable().optional(),
});

const agentPriceCard = z.object({
  type: z.literal(ComponentsTypes.AGENT_PRICE_CARD),
  price_estimate_modal_id: z.string().nullable(),
  payment_details_modal_id: z.string().nullable(),
  quote_kind: z.enum(QuoteKind).nullable(),
  quote_status: z.enum(QuoteStatus).nullable(),
  invoice_period: z.enum(PricingTermArray),
  price_change_action: z.string().nullable().optional(),
});

// Add new variants here
const VARIANTS = [
  simpleComponent,
  modalSchema,
  inputForm,
  actionForm,
  bindForm,
  policyEffectiveDatePicker,
  layoutSchema,
  entityValidityRenderer,
  carousel,
  carouselTile,
  accordion,
  accordionList,
  tabList,
  tabPanel,
  expandableContainer,
  expandableList,
  dynamicLayout,
  slot,
  list,
  card,
  cardContent,
  readMore,
  fixedContainer,
  sectionContainer,
  coverageSceneWrapper,
  mockDataWrapper,
  coverageSection,
  lienholderConfirmationModal,
  validationErrorModal,
  agentPriceCard,
] as const;

type ComponentInner = z.ZodDiscriminatedUnion<typeof VARIANTS>;

export const component: ComponentInner = z.discriminatedUnion('type', VARIANTS);

const components = z.array(component);

export function isField(component: Component): component is Field {
  return field.safeParse(component).success;
}

export function isCoverageField(component: Component): component is CoverageField {
  return coverageField.safeParse(component).success;
}

const sceneSchema = z.object({
  redirect_to: z.string().nullable(),
  polling_rate: z.number().nullable(),
  polling_timeout: z.number().nullable(),
  name: z.string(),
  components,
  variant: z.string(),
  event_communicator: eventCommunicator.nullable(),
});

const scene_data_element = z.object({
  attribute: z.string(),
  scope: z.string(),
  entity_index: z.number(),
  message: z.string().nullable(),
  valid: z.boolean(),
  masked: z.boolean(),
  validation_rules: z.array(validationRule),
  value: z.union([z.string(), z.number(), z.boolean()]).nullable(),
});

const price_data = z.object({
  details: priceCardDetails,
  get paymentModalContent() {
    return components;
  },
});

const stepData = z.object({
  scene_data: z.array(scene_data_element).nullable(),
  coverages: z.array(coverage).nullable(),
  price_data: price_data.nullable(),
});

export const sceneConfigSchema = z.object({
  scene: sceneSchema,
  entity_index: z.number(),
  experience: z.string(),
  step_id: z.string(),
  // Temporary until deploy
  step_state: stepData.nullable(),
  quote_id: z.string().nullable(),
  agent_id: z.string().nullable(),
  agency_id: z.string().nullable(),
  market: z.string().nullable(),
  experiments: z.record(z.string(), z.string()),
});

export type FieldType = Field['type'];
export type CoverageFieldType = CoverageField['type'];
export type SceneData = z.infer<typeof sceneConfigSchema>;
export type Scene = z.infer<typeof sceneSchema>;
export type StandardDivider = z.infer<typeof standardDivider>;
export type AccentDivider = z.infer<typeof accentDivider>;
export type Component = z.infer<typeof component>;
export type Components = z.infer<typeof components>;
export type Card = z.infer<typeof card>;
export type CardContent = z.infer<typeof cardContent>;
export type ReadMore = z.infer<typeof readMore>;
export type ActionForm = z.infer<typeof actionForm>;
export type PolicyEffectiveDatePicker = z.infer<typeof policyEffectiveDatePicker>;
export type Price = z.infer<typeof price>;
export type RichText = z.infer<typeof richText>;
export type Field = z.infer<typeof field>;
export type CoverageField = z.infer<typeof coverageField>;
export type Form = z.infer<typeof form>;
export type BindForm = z.infer<typeof bindForm>;
export type Button = z.infer<typeof button>;
export type NavigationButton = z.infer<typeof navigationButton>;
export type SubmitButton = z.infer<typeof submitButton>;
export type ValidationModalSubmitButton = z.infer<typeof validationModalSubmitButton>;
export type ExternalImage = z.infer<typeof externalImage>;
export type CircularProgress = z.infer<typeof circularProgress>;
export type Alert = z.infer<typeof alert>;
export type Toast = z.infer<typeof toast>;
export type ModalButton = z.infer<typeof modalButton>;
export type ActionButton = z.infer<typeof actionButton>;
export type HrefButton = z.infer<typeof hrefButton>;
export type BackButton = z.infer<typeof backButton>;
export type CoverageSubmitButton = z.infer<typeof coverageSubmitButton>;
export type NavigationLink = z.infer<typeof navigationLink>;
export type ModalLink = z.infer<typeof modalLink>;
export type Typography = z.infer<typeof typography>;
export type InertListItem = z.infer<typeof inertListItem>;
export type NavigationListItem = z.infer<typeof navigationListItem>;
export type HrefListItem = z.infer<typeof hrefListItem>;
export type ToggleListItem = z.infer<typeof toggleListItem>;
export type CookieManagementLink = z.infer<typeof cookieManagementLink>;
export type HrefLink = z.infer<typeof hrefLink>;
export type ScrollLink = z.infer<typeof scrollLink>;
export type DateInputSegmented = z.infer<typeof dateInputSegmented>;
export type DatePicker = z.infer<typeof datePicker>;
export type TextInput = z.infer<typeof textInput>;
export type Address = z.infer<typeof address>;
export type AltAddress = z.infer<typeof altAddress>;
export type AnnualizedMileage = z.infer<typeof annualizedMileage>;
export type BraintreeInput = z.infer<typeof braintreeInput>;
export type EmailInput = z.infer<typeof emailInput>;
export type PhoneInput = z.infer<typeof phoneInput>;
export type NumberInput = z.infer<typeof numberInput>;
export type Radio = z.infer<typeof radio>;
export type ChipGroup = z.infer<typeof chipGroup>;
export type CoverageChipGroup = z.infer<typeof coverageChipGroup>;
export type CoverageSelectDropdown = z.infer<typeof coverageSelectDropdown>;
export type CoverageCheckbox = z.infer<typeof coverageCheckbox>;
export type ReadonlyCoverageValue = z.infer<typeof readonlyCoverageValue>;
export type ReadonlyCoverageCheckbox = z.infer<typeof readonlyCoverageCheckbox>;
export type ReadonlyCoverageChipGroup = z.infer<typeof readonlyCoverageChipGroup>;
export type ReadonlyTextInput = z.infer<typeof readonlyTextInput>;
export type ReadonlyDateInputSegmented = z.infer<typeof readonlyDateInputSegmented>;
export type ReadonlySelectDropdown = z.infer<typeof readonlySelectDropdown>;
export type SectionContainer = z.infer<typeof sectionContainer>;
export type SelectDropdown = z.infer<typeof selectDropdown>;
export type AutocompleteDropdown = z.infer<typeof autocompleteDropdown>;
export type Option = z.infer<typeof option>;
export type Checkbox = z.infer<typeof checkbox>;
export type RichTextCheckbox = z.infer<typeof richTextCheckbox>;
export type Switch = z.infer<typeof switchSelect>;
export type ComponentValue = Field['value'];
export type Validation = z.infer<typeof validationRule>;
export type InfoCard = z.infer<typeof infoCard>;
export type ReloadButton = z.infer<typeof reloadButton>;
export type Step = z.infer<typeof step>;
export type SteppedLoader = z.infer<typeof steppedLoader>;
export type NavigationStepper = z.infer<typeof navigationStepper>;
export type CoverageSection = z.infer<typeof coverageSection>;
export type PriceCard = z.infer<typeof priceCard>;
export type AgentPriceCard = z.infer<typeof agentPriceCard>;
export type StepPriceData = NonNullable<z.infer<typeof price_data>>;
export type PartnerCommitmentPrice = z.infer<typeof partnerCommitmentPrice>;
export type PartnerCommitmentPriceCard = z.infer<typeof partnerCommitmentPriceCard>;
export type MediaAlphaResell = z.infer<typeof mediaAlphaResell>;
export type PrefillPayload = z.infer<typeof prefillSchema>;
export type Typographies = z.infer<typeof typographies>;
export type Modal = z.infer<typeof modalSchema>;
export type LienholderConfirmationModal = z.infer<typeof lienholderConfirmationModal>;
export type ValidationErrorModal = z.infer<typeof validationErrorModal>;
export type SearchBar = z.infer<typeof searchBar>;
export type EntityValidityRenderer = z.infer<typeof entityValidityRenderer>;
export type EntityInProgressChip = z.infer<typeof entityInProgressChip>;
export type SceneDataElement = z.infer<typeof scene_data_element>;
